import type { Price } from "../model/Prices";
import { CommonCartItem } from "../model/CommonCartTypes";
import type { CheckoutData, CheckoutItem } from "../model/CheckoutData";
import type { CommonCart } from "../model/CommonCartTypes";
import { trackStructured, trackUiError } from "../utils/TrackingUtil";
import type { AnonCart } from "../model/AnonCartTypes";
export type ProductType = "regular" | "weighted" | "weight-per-piece";
export type GA360TrackProduct = {
	name: string;
	id: string;
	price: string | null;
	brand?: string | null;
	variant?: string | null;
	quantity: number;
	category?: string | null;
	metric3?: string | null;
	// stroke price
	metric6?: string | null;
	// weight
	dimension38?: ProductType;
	dimension42?: string | null;
	// subsys
	dimension61?: string | null;
	// MGB
	dimension62?: string | null; // unit size
};
export type GA360CheckoutStepTrackObject = {
	event: "checkout";
	ecommerce: {
		currencyCode: string;
		checkout?: {
			actionField: {
				step: number;
			};
			products?: Array<GA360TrackProduct> | null;
		};
	};
};
export type GA360PurchaseTrackObject = {
	event: "purchase";
	ecommerce: {
		currencyCode: string;
		purchase: {
			actionField: {
				id: string;
				step: number;
				revenue: string;
				shipping: string;
				tax: string;
				metric4: number | null; // deposit
			};
			products: Array<GA360TrackProduct> | null | undefined;
		};
	};
};
export type GA360RemoveFromCartObject = {
	event: "removeFromCart";
	ecommerce: {
		currencyCode: string;
		remove: {
			actionField: {
				list: "Shopping cart";
			};
			products: Array<GA360TrackProduct> | null | undefined;
		};
	};
};
export class TrackingStoreGAUtils {
	static getCheckoutPageStep(page: string) {
		switch (page.toUpperCase()) {
			case "DELIVERY":
				return 1;

			case "PAYMENT":
				return 2;

			case "SUMMARY":
				return 3;

			case "CONFIRMATION":
				return 4;

			default:
				return 0;
		}
	}

	static getPrice = (price: Price | null | undefined, item: CommonCartItem | CheckoutItem): string | null => {
		const amount = price && price.net && price.net.amount;
		if (!amount) return null;
		// @ts-expect-error
		if (!item.isWeighted) return amount.toString();
		if (item.weightPerPiece) return (item.weightPerPiece.value * amount).toString();
		return (amount / 1000).toString();
	};
	static getQuantity = (item: CommonCartItem | CheckoutItem) => {
		// @ts-expect-error
		if (!item.isWeighted) return item.quantity;
		if (item.weightPerPiece) return item.quantity / item.weightPerPiece.value;
		return item.quantity * 1000;
	};

	//GA item remove tracking: can not add it to TrackingStore.trackItemRemoved, the cart items are already removed when calling this._anonCartStore.getCart()
	//or this._customerCartStore.getCart() in TrackingStore
	static trackGAItemRemoved(
		bundleId: string,
		cart: AnonCart | CommonCart | null,
		trackingContext: Record<string, any>,
	) {
		if (!cart) {
			return;
		}
		const gtmTrackingData = TrackingStoreGAUtils.getGATrackingRemoveFromCartObject([bundleId], cart);

		if (!gtmTrackingData) {
			return;
		}

		trackStructured(
			"cart.item.delete",
			{
				tags: {
					bettyBundleId: bundleId,
					source: "cart",
					...trackingContext,
				},
			},
			// @ts-expect-error
			gtmTrackingData,
		);
	}

	static getGATrackingRemoveFromCartObject(
		bundleIds: string[],
		cart: AnonCart | CommonCart,
	): GA360RemoveFromCartObject | null | undefined {
		try {
			const items = cart.items;
			const totalPrices = cart.totalPrices;
			const currencyCode =
				(totalPrices && totalPrices.total && totalPrices.total.gross && totalPrices.total.gross.currency) ||
				"no-currencyCode";
			const products: Array<GA360TrackProduct> = bundleIds
				.map((bundleId: string) => items[bundleId])
				.filter((item) => !!item)
				.map((item) => {
					const { article: articlePrice } =
						item.prices && item.prices.bundle ? item.prices.bundle : { article: null };
					return {
						name: item.description,
						id: item.bundleId,
						price: TrackingStoreGAUtils.getPrice(articlePrice, item as any),
						quantity: TrackingStoreGAUtils.getQuantity(item as any),
						category:
							Array.isArray(item.categories) && item.categories.length > 0
								? item.categories.join(",")
								: null,
					};
				});

			if (!products || !products.length) {
				return undefined;
			}

			return {
				event: "removeFromCart",
				ecommerce: {
					currencyCode,
					remove: {
						actionField: {
							list: "Shopping cart",
						},
						products: products,
					},
				},
			};
		} catch (e) {
			console.log(e);
			trackUiError(
				"TrackingStore",
				`Could not construct GA tracking remove from cart object: ${(e as any).message}`,
			);
			return undefined;
		}
	}

	static getGATrackingCheckoutObject(
		checkoutData: CheckoutData,
		page: string,
	): GA360CheckoutStepTrackObject | null | undefined {
		try {
			const step = TrackingStoreGAUtils.getCheckoutPageStep(page);

			if (step === 0) {
				return undefined;
			}

			const products = TrackingStoreGAUtils.getGATrackingProducts(checkoutData);

			if (!products || !products.length) {
				return undefined;
			}

			const { total } = checkoutData.checkoutPrices.total || {};
			const currencyCode = (total && total.gross && total.gross.currency) || "no-currencyCode";
			const checkoutPayload = {
				checkout: {
					actionField: {
						step,
					},
					products,
				},
			};
			return {
				event: "checkout",
				ecommerce: {
					currencyCode,
					...checkoutPayload,
				},
			};
		} catch (e) {
			console.log(e);
			trackUiError("TrackingStore", `Could not construct GA tracking checkout object: ${(e as any).message}`);
			return undefined;
		}
	}

	static getGATrackingPurchaseObject(checkoutData: CheckoutData): GA360PurchaseTrackObject | null | undefined {
		try {
			const step = TrackingStoreGAUtils.getCheckoutPageStep("confirmation");
			const products = TrackingStoreGAUtils.getGATrackingProducts(checkoutData);

			if (!products || !products.length) {
				return undefined;
			}

			const {
				total,
				paymentRemaining,
				shipping: shippingTotal,
				basket,
			} = checkoutData.checkoutPrices.total || {};
			const currencyCode = total?.gross?.currency || "no-currencyCode";
			const revenue =
				paymentRemaining?.gross?.amount?.toString() || total?.gross?.amount?.toString() || "no-revenue";
			const shipping = shippingTotal?.total?.gross?.amount?.toString() || "no-shipping";
			const tax = total?.vat?.amount?.toString() || "no-tax";
			const deposit = basket?.articles?.empties?.gross?.amount || null;
			const purchasePayload = {
				purchase: {
					actionField: {
						id: checkoutData.id,
						step,
						revenue,
						shipping,
						tax,
						metric4: deposit,
					},
					products: products,
				},
			};
			return {
				event: "purchase",
				ecommerce: {
					currencyCode,
					...purchasePayload,
				},
			};
		} catch (e) {
			console.log(e);
			trackUiError("TrackingStore", `Could not construct GA tracking purchase object: ${(e as any).message}`);
			return undefined;
		}
	}

	static getGATrackingProducts(checkoutData: CheckoutData): Array<GA360TrackProduct> | null | undefined {
		return Object.values(checkoutData.items).map((item: CheckoutItem) => {
			const { article: articlePrice, strikeThrough: strikeThroughPrice } = item.prices?.bundle ?? {
				article: null,
				strikeThrough: null,
			};
			return {
				name: item.description,
				id: item.bundleId,
				dimension38: item.isWeighted ? (item.weightPerPiece ? "weight-per-piece" : "weighted") : "regular",
				dimension42: item.subsys || "no-idSubsys",
				dimension61: item.trackingId || "no-idMgb",
				dimension62: item.bundleSize || "no-unitSize",
				price: TrackingStoreGAUtils.getPrice(articlePrice, item),
				metric3: TrackingStoreGAUtils.getPrice(strikeThroughPrice, item),
				metric6: item.isWeighted
					? ((item.weightPerPiece && item.weightPerPiece.value) || 0.001).toString()
					: null,
				quantity: TrackingStoreGAUtils.getQuantity(item),
			};
		});
	}
}
